import React from 'react';
import '../styles/SourceBadge.css';
import { Zillow, Redfin, Realtor, GoogleDrive, Dropbox, OneDrive, ExternalLink } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';
import { FaviconImg } from './SourceIcon';
import { getFaviconSrc, getSiteName, hostFromUrl, faviconUrlForHost } from '../lib/sourceFaviconUtils';

// Renders a small provenance badge for a property image. `source` is the
// descriptor from data.imageSources: { kind, originUrl, platform? }.
// `sourceFavicons` is the lead's data.sourceFavicons map — for scraped images
// whose originUrl host is a known deal-page host, the wholesaler's favicon is
// shown instead of the generic external-link glyph.
// Returns null for unknown/missing sources so callers can render unconditionally.

const MLS_ICON = { zillow: Zillow, redfin: Redfin, realtor: Realtor };

const KIND_ICON = {
    drive: GoogleDrive,
    dropbox: Dropbox,
    oneDrive: OneDrive,
    scraped: ExternalLink,
};

const KIND_LABEL = {
    drive: 'Drive',
    dropbox: 'Dropbox',
    oneDrive: 'OneDrive',
    zip: 'ZIP',
    scraped: 'Link',
    mls: 'MLS',
};

const PLATFORM_LABEL = { zillow: 'Zillow', redfin: 'Redfin', realtor: 'Realtor', googleMaps: 'Maps' };

function SourceBadge({ source, iconOnly = false, sourceFavicons = null }) {
    if (!source || !source.kind || !KIND_LABEL[source.kind]) return null;

    const { kind, originUrl, platform } = source;

    // Scraped images: show the source site's real favicon instead of the generic
    // external-link glyph. Prefer the wholesaler's named favicon from the lead's
    // sourceFavicons map; when that map is absent/empty (e.g. MCP-submitted deals)
    // fall back to deriving the favicon straight from the originUrl host via the
    // Google Favicon API — the same approach the main page uses (ProviderIcon
    // faviconFallback). FaviconImg itself falls back to the generic external-link
    // icon if the favicon fails to load, so this is safe for any host.
    const scrapedHost = kind === 'scraped' ? hostFromUrl(originUrl) : null;
    const faviconSrc = scrapedHost
        ? (getFaviconSrc(scrapedHost, sourceFavicons) || faviconUrlForHost(scrapedHost))
        : null;

    const Icon = kind === 'mls'
        ? (platform ? MLS_ICON[platform] : null)
        : (kind === 'scraped' && faviconSrc ? null : (KIND_ICON[kind] || null));
    const label = kind === 'mls'
        ? (PLATFORM_LABEL[platform] || 'MLS')
        : (kind === 'scraped' && faviconSrc ? getSiteName(scrapedHost, sourceFavicons) : KIND_LABEL[kind]);

    // Brand logos carry their own colours; the monochrome scraped icon needs a
    // white stroke to read on the dark badge.
    const iconProps = kind === 'scraped' ? { fill: colour.gray00 } : {};

    const stop = (e) => e.stopPropagation();
    const inner = (
        <>
            {faviconSrc ? <FaviconImg src={faviconSrc} alt={label} fallbackFill={colour.gray00} /> : (Icon ? <Icon {...iconProps} /> : null)}
            {!iconOnly && <span className="xs-label-bold colour-white">{label}</span>}
        </>
    );

    const className = `source-badge${iconOnly ? ' source-badge-icon-only' : ''}`;

    if (originUrl && /^https?:\/\//i.test(originUrl)) {
        return (
            <a
                className={`${className} no-underline`}
                href={originUrl}
                target="_blank"
                rel="noreferrer"
                onClick={stop}
                aria-label={`Image source: ${label}`}
            >
                {inner}
            </a>
        );
    }
    return (
        <span className={className} aria-label={`Image source: ${label}`}>
            {inner}
        </span>
    );
}

export default SourceBadge;
